import { Badge } from 'features/catalog/useBadges';
import { BADGE_GROUP_BADGE_IDS, BADGE_GROUPS } from 'features/catalog/badge-groups';

/**
 * Badge IDs retired from the live catalog (commented out in badge-definitions).
 * Profiles may still carry them; treat as if they never existed for edit diffs,
 * feed updates, and submissions — silently omit, never show as removals.
 */
export const RETIRED_BADGE_IDS = new Set([
  'herm', // Traveler I
  '20c8', // Traveler III
  'pond', // Pond Royalty
  'hotl', // Hot Lap Challenge Participant
  'rac3', // Talos Racing Dead Last
  'coi2', // Golden Handoff II
  '1tgz', // Retreat Ranger
]);

/** Profile badge IDs with retired catalog entries stripped. */
export const activeProfileBadgeIDs = (badgeIDs: string[] | null | undefined): string[] =>
  (badgeIDs ?? []).filter((id) => !RETIRED_BADGE_IDS.has(id));

/** Profile badge IDs that still exist in the live catalog (drops retired + unknown). */
export const liveCatalogBadgeIDs = (
  badgeIDs: string[] | null | undefined,
  badgesMap: ReadonlyMap<string, Badge>
): string[] => (badgeIDs ?? []).filter((id) => badgesMap.has(id));

export type EditSummaryBadge = {
  id: string;
  name: string;
  rarityName: Badge['rarityName'];
};

/**
 * Badges the profile had that are no longer enabled.
 * Retired IDs are ignored — they are dropped from paste as if they never existed.
 */
export const getRemovedBadgesForEditSummary = (
  profileBadgeIDs: string[] | null | undefined,
  enabledBadgesMap: Map<string, boolean>,
  badgesMap: Map<string, Badge>
): EditSummaryBadge[] =>
  activeProfileBadgeIDs(profileBadgeIDs)
    .filter((id) => !enabledBadgesMap.get(id))
    .map((id) => {
      const badge = badgesMap.get(id);
      if (badge) {
        return { id: badge.id, name: badge.name, rarityName: badge.rarityName };
      }
      return {
        id,
        name: `Unknown badge (${id})`,
        rarityName: 'common' as const,
      };
    });

/**
 * Builds an initial enabled/disabled map for all known badges based on a
 * list of badge IDs that should be marked as enabled (e.g. a profile's
 * collected badges). Badges not in any group ("freeform") are included too.
 *
 * Used by ClaimBadgesView / BadgeGroupsComponent edit flows.
 */
export const getInitialBadgeEnabledState = (
  badgeIDs: string[] | null,
  badgesMap: Map<string, Badge>
): Map<string, boolean> => {
  const enabledBadgeIDs = new Set(badgeIDs ?? []);
  const map = BADGE_GROUPS.reduce((acc, group) => {
    group.badgesIDs.forEach((id) => {
      acc.set(id, enabledBadgeIDs.has(id));
    });
    return acc;
  }, new Map<string, boolean>());

  // Add freeform badges (not part of any group) to the initial state
  badgesMap.forEach((_, id) => {
    if (!BADGE_GROUP_BADGE_IDS.has(id)) {
      map.set(id, enabledBadgeIDs.has(id));
    }
  });

  return map;
};
